import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    AttachmentHandler,
    CurrentPersonDto,
    DO_NOTHING,
    FlyFreelyError,
    FlyFreelyLoggingService,
    Organisation,
    PersonService,
    PersonWithRoles,
    WorkTracker,
    fromLocalDate
} from '@flyfreely-portal-ui/flyfreely';
import {
    NgSelectPaginationValues,
    NgSelectSearch,
    ngSelectPreSearchConfigure,
    updateNgSelectData
} from '@flyfreely-portal-ui/ng-select';
import { personSearch } from '@flyfreely-portal-ui/ui';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import { LinkAction } from 'libs/attachments/src/lib/link-manager/link-manager.interface';
import { AttachmentHandlerService } from 'libs/comments/src/lib/attachment-handler.service';
import {
    Comment,
    CommentDelete,
    CommentUpdate
} from 'libs/comments/src/lib/interfaces';
import { GeneralTask } from 'libs/flyfreely/src/lib/services/generalTasks.service';
import {
    GeneralTasksDataService,
    formatGeneralTasksPriority,
    formatGeneralTasksStatuses
} from 'libs/outstanding-tasks/src/lib/widgets/general-task/general-tasks-data.service';
import { Delta } from 'quill';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'general-task-edit',
    templateUrl: './general-task-edit.component.html',
    styleUrls: ['./general-task-edit.component.css']
})
export class GeneralTaskEditComponent implements OnInit, OnDestroy {
    @Input() generalTaskId: number;
    @Output() editingCancelled = new EventEmitter<void>();
    @Output() taskUpdated = new EventEmitter<GeneralTask>();

    generalTask: GeneralTask;
    organisation: Organisation;
    comments: Comment[];
    currentUser: CurrentPersonDto | undefined;
    attachmentsHandler: AttachmentHandler;
    editGeneralTaskForm: FormGroup;

    placeholderText =
        'Add a comment. You can tag people with "@", RPA with "#RPA", and batteries with "#BATTERY"';

    personList: PersonWithRoles[] = [];
    loadingPersonnel = false;

    private personSearchSource = new Subject<NgSelectSearch>();

    personSelectConfig: NgSelectPaginationValues = {
        currentPage: 0,
        preSearchPage: null,
        searchValue: null,
        totalItems: null
    };

    priorityOptions = Object.entries(formatGeneralTasksPriority).map(
        ([value, label]) => ({ label, value })
    );
    statusOptions = Object.entries(formatGeneralTasksStatuses).map(
        ([value, label]) => ({ label, value })
    );

    protected readonly LinkAction = LinkAction;
    protected readonly personSearch = personSearch;

    working = false;
    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();
    constructor(
        private personService: PersonService,
        private generalTasksDataService: GeneralTasksDataService,
        private workspaceStateService: WorkspaceStateService,
        private commonDialoguesService: CommonDialoguesService,
        private logging: FlyFreelyLoggingService,
        private attachmentHandlerService: AttachmentHandlerService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit(): void {
        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation.organisation;
                this.refreshData();
                this.attachmentsHandler =
                    this.generalTasksDataService.attachmentHandler(
                        this.generalTaskId,
                        this.organisation.id
                    );
                this.attachmentHandlerService.attachmentHandler =
                    this.attachmentsHandler;
                this.generalTasksDataService.commentChange$
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe(() => {
                        this.refreshComments();
                    });
            });

        this.personSearchSource
            .pipe(debounceTime(300), takeUntil(this.ngUnsubscribe$))
            .subscribe(search => {
                this.personSelectConfig = ngSelectPreSearchConfigure(
                    search,
                    this.personSelectConfig
                );
                this.refreshPersonList();
            });
    }

    ngOnDestroy(): void {
        this.personSearchSource.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private refreshPersonList(preloadIds?: number[]) {
        if (!this.organisation) {
            return;
        }

        this.loadingPersonnel = true;

        this.personService
            .findPersonnelGraphQL(
                this.organisation.id,
                this.personSelectConfig.currentPage,
                15,
                {
                    textSearch:
                        this.personSelectConfig.searchValue || undefined,
                    idList: preloadIds
                }
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result => {
                    const newPeople = result.results.filter(
                        p =>
                            this.personList.findIndex(
                                person => person.id === p.id
                            ) === -1
                    );
                    this.personList = this.personList.concat(newPeople);

                    if (preloadIds == null) {
                        this.personSelectConfig = updateNgSelectData(
                            result,
                            this.personSelectConfig
                        );
                    }

                    this.loadingPersonnel = false;
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error loading personnel: ${error.message}`
                    );
                    this.loadingPersonnel = false;
                }
            })
            .add(this.workTracker.createTracker());
    }

    onPersonSelectScroll() {
        const resultLength =
            this.personSelectConfig.searchValue == null
                ? this.personList.length
                : this.personList.filter(person =>
                      this.personSearch(
                          this.personSelectConfig.searchValue,
                          person
                      )
                  ).length;

        if (
            this.personSelectConfig.totalItems == null ||
            resultLength !== this.personSelectConfig.totalItems
        ) {
            this.refreshPersonList();
        }
    }

    onPersonSelectSearch(search: NgSelectSearch) {
        this.personSearchSource.next(search);
    }

    onPersonSelectClose() {
        this.personSelectConfig.searchValue = null;
    }

    onPersonSelectOpen() {
        if (
            this.personSelectConfig.totalItems == null ||
            this.personList.length !== this.personSelectConfig.totalItems
        ) {
            this.refreshPersonList();
        }
    }

    refreshData() {
        this.generalTasksDataService
            .findTaskById(this.generalTaskId)
            .subscribe({
                next: result => {
                    this.generalTask = result;
                    this.initEditForm();
                    this.refreshComments();

                    if (this.generalTask.assignee?.id) {
                        this.refreshPersonList([this.generalTask.assignee.id]);
                    } else {
                        this.refreshPersonList();
                    }
                }
            })
            .add(this.workTracker.createTracker());
    }

    initEditForm(): void {
        this.editGeneralTaskForm = new FormGroup({
            summary: new FormControl(
                this.generalTask?.summary,
                Validators.required
            ),
            description: new FormControl(
                this.generalTask?.description,
                Validators.required
            ),
            dueDate: new FormControl(
                this.generalTask.dueDate
                    ? fromLocalDate(this.generalTask.dueDate)
                    : null
            ),
            priority: new FormControl(
                this.generalTask?.priority,
                Validators.required
            ),
            assignee: new FormControl(this.generalTask?.assignee?.id),
            status: new FormControl(
                this.generalTask.status,
                Validators.required
            )
        });
    }

    onCancel() {
        if (this.editGeneralTaskForm?.dirty) {
            this.confirmCancel();
        } else {
            this.cancelEdit();
        }
    }

    confirmCancel() {
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Confirm Cancel',
                `You have unsaved changes, are you sure you want to cancel?`,
                'Yes',
                () => Promise.resolve()
            )
            .then(() => this.cancelEdit(), DO_NOTHING);
    }

    cancelEdit() {
        this.editingCancelled.emit();
    }

    onSave() {
        const value = this.editGeneralTaskForm.value;
        this.generalTasksDataService
            .updateTask({
                taskId: this.generalTask.id,
                summary: value.summary,
                description: value.description,
                dueDate: value.dueDate,
                assigneeId: value.assignee,
                priority: value.priority,
                status: value.status
            })
            .subscribe({
                next: newTask => {
                    this.logging.success(`${value.summary} updated`);
                    this.taskUpdated.emit(newTask);
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while updating task: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    refreshComments() {
        this.generalTasksDataService
            .findComments(this.generalTask.id)
            .subscribe(res => {
                this.comments = res as unknown as Comment[];
            });
    }

    onCreateComment(content: Delta) {
        this.generalTasksDataService
            .createComment(this.generalTask.id, content)
            .subscribe({
                next: () => {
                    this.logging.success(`Comment created`);
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while creating comment: ${error.message}`
                    );
                }
            });
    }

    onUpdateComment($event: CommentUpdate) {
        this.generalTasksDataService
            .updateComment(
                this.generalTask.id,
                $event.commentId,
                $event.content
            )
            .subscribe({
                next: () => {
                    this.logging.success(`Comment updated`);
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while updating comment: ${error.message}`
                    );
                }
            });
    }

    onDeleteComment($event: CommentDelete) {
        this.generalTasksDataService
            .deleteComment(this.generalTask.id, $event.commentId)
            .subscribe({
                next: () => {
                    this.logging.success(`Comment deleted`);
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while deleting comment: ${error.message}`
                    );
                }
            });
    }
}
